import { Controller } from "@hotwired/stimulus"

// One card's two panels — its chart and its log form — behind one row of
// buttons.
//
// This was two <details> elements, which was less code and needed no
// JavaScript. It could not produce the layout: a <details> is one box, so
// its summary and its panel are stuck in the same column. Two of them side
// by side put each panel under its own half of the card, and stacked they
// made a column of buttons down the bottom of every card.
//
// The buttons belong in one row and the panels belong full width beneath
// them, which means the button cannot be the panel's parent.
//
// Progressive enhancement is kept by the markup, not by this file: both
// panels are rendered open and this closes them on connect. With no
// JavaScript you get a long card showing everything, which is the old
// behaviour rather than a broken one.
export default class extends Controller {
  static targets = ["panel", "button"]

  connect() {
    this.closeAll()
  }

  toggle(event) {
    const button = event.currentTarget
    const panel = this.panelTargets.find((p) => p.dataset.panel === button.dataset.panel)
    if (!panel) return

    const opening = panel.hasAttribute("hidden")

    // One at a time. Both open makes the card taller than the two beside it
    // and pushes the other card's controls out of alignment — and you are
    // either looking at the history or recording a reading, not usually
    // both.
    this.closeAll()
    if (!opening) return

    panel.removeAttribute("hidden")
    button.setAttribute("aria-expanded", "true")
    // The button for what is ALREADY showing becomes the way to put it away.
    // Otherwise the card offers "View progress" while the progress is on
    // screen, which reads as a button that does nothing.
    this.setLabel(button, button.dataset.openLabel)
    this.focusFirstField(panel)
  }

  // NEVER THE RAW <select>, which is what opens the OS picker.
  //
  // This was `querySelector("input, select")`. On the Body page a :choice
  // metric's panel renders shared/_select_search, which deliberately leaves
  // the real <select> VISIBLE and focusable as the no-JS fallback and hides
  // it only in select_search_controller#connect. So tapping "Log new" on
  // Activity level focused that select and opened the native picker — and
  // because it is fully populated with five choices it did not look broken,
  // it looked like the app uses an OS dropdown on that one card.
  //
  // The same failure as ingredients_controller#focusFirstField, fixed 15 Sep
  // (acfbfc1); that fix was never swept for other copies of the selector.
  // CLAUDE.md calls "no native select lists" non-negotiable, and this is
  // that rule breaking at RUNTIME rather than in markup, which is why
  // neither the styleguide spec nor a request spec could see it.
  //
  // The searchable input by preference, then any text input; a select is not
  // a candidate at all.
  focusFirstField(panel) {
    const search = panel.querySelector("[data-select-search-target='input']")

    ;(search || panel.querySelector("input:not([type=hidden])"))?.focus()
  }

  closeAll() {
    this.panelTargets.forEach((panel) => panel.setAttribute("hidden", ""))
    this.buttonTargets.forEach((button) => {
      button.setAttribute("aria-expanded", "false")
      this.setLabel(button, button.dataset.closedLabel)
    })
  }

  // Only the label span, so the icon beside it survives.
  setLabel(button, text) {
    if (!text) return
    const label = button.querySelector("[data-label]")
    if (label) label.textContent = text
  }
}
